import { Badge, Button, EmptyState, LoadingRows } from '@shared/ui';
import { Fragment, useState } from 'react';

import { previewSummary } from '../model/columns';
import { recordModuleTag, rowRecordCells, runModuleTag } from '../model/module-catalogue';
import { moduleReportView } from '../model/module-report';
import {
    notMigratedByDesignLine,
    notMigratedByDesignSummary,
    type NotMigratedByDesignSummary,
} from '../model/not-migrated-by-design';
import { outcomeReason } from '../model/outcome-copy';
import { rowMessage, warningLabel } from '../model/row-warnings';
import {
    plannedWarningLine,
    plannedWarningsShown,
    recordIssueLines,
    rowsTableShape,
    rowsTableSpan,
} from '../model/rows-table';
import { issuesText, recordIssues, runStatusText, runTotalsText } from '../model/run-summary';
import { isSelectable, isSelected, type Selection } from '../model/selection';

import { OutcomeBadge } from './OutcomeBadge';
import { Td, Th } from './table';

import type {
    BulkModule,
    JobMode,
    RecordRow,
    RowWarning,
    TenantRow,
} from '../schemas/bulk-migration';

/**
 * One row per client of a job. Two layouts over one table (`rowsTableShape`):
 *
 * - **analysis** — what would be migrated: the module's own columns (from the
 *   first record; the rest on expand), the Branding preview when the module has
 *   one, and a checkbox on every migratable client;
 * - **migration** — what happened: the run's status, its totals and the
 *   warnings and failures on the records the row carries. A module that opts in
 *   (`launchColumns`, Company Information) also shows its columns and expands
 *   each record with its warnings, next to the values a warning points at.
 *
 * An expanded record also lists the warnings the analysis planned for it
 * (`plannedWarnings`); a status refresh (`mode: 'report'`) leaves them off the
 * records that are `Migrated`.
 *
 * A composite's row is one per client, its records the union of its members',
 * each tagged with the member it belongs to; a column the record's member does
 * not have reads blank.
 */

const NO_TITLES: ReadonlyMap<string, string> = new Map();
export function TenantRowsTable({
    module,
    layout,
    mode,
    memberTitles = NO_TITLES,
    rows,
    isLoading,
    hasMore,
    loadingMore,
    onLoadMore,
    selection,
    onToggle,
}: {
    module: BulkModule;
    layout: 'analysis' | 'migration';
    /** The mode of the job whose rows these are. */
    mode: JobMode;
    /** A composite's member titles, for the tag on each record. */
    memberTitles?: ReadonlyMap<string, string> | undefined;
    rows: readonly TenantRow[];
    isLoading: boolean;
    hasMore: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
    selection?: Selection | undefined;
    onToggle?: ((row: TenantRow) => void) | undefined;
}) {
    const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
    const selectable = selection !== undefined && onToggle !== undefined;
    const shape = rowsTableShape(module, layout, moduleReportView(module.moduleId).launchColumns);
    const { columns, preview } = shape;
    const span = rowsTableSpan(shape, selectable);

    if (isLoading) return <LoadingRows label="Loading clients…" />;
    if (rows.length === 0) {
        return <EmptyState title="No clients here" description="Try another outcome or search." />;
    }

    const flip = (tenantId: string) => {
        setExpanded((current) => {
            const next = new Set(current);
            if (next.has(tenantId)) next.delete(tenantId);
            else next.add(tenantId);
            return next;
        });
    };

    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse">
                <thead>
                    <tr>
                        {selectable && <Th className="w-10" />}
                        <Th>Client</Th>
                        <Th>Outcome</Th>
                        {columns.map((column) => (
                            <Th
                                key={column.key}
                                className={column.align === 'right' ? 'text-right' : undefined}
                            >
                                {column.label}
                            </Th>
                        ))}
                        {preview && <Th>Preview</Th>}
                        {shape.trailing.map((header) => (
                            <Th key={header}>{header}</Th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const id = row.tenant.tenantId;
                        const first = row.records[0];
                        const isOpen = expanded.has(id);
                        const issues = recordIssues(row);
                        const reason = outcomeReason(row);
                        const { warnings } = rowMessage(row);
                        return (
                            <Fragment key={id}>
                                <tr className="hover:bg-surface-sunken">
                                    {selectable && (
                                        <Td>
                                            <input
                                                type="checkbox"
                                                aria-label={`Select ${row.tenant.displayName}`}
                                                checked={isSelected(selection, row)}
                                                disabled={!isSelectable(row)}
                                                onChange={() => {
                                                    onToggle(row);
                                                }}
                                                className="size-4 accent-brand"
                                            />
                                        </Td>
                                    )}
                                    <Td>
                                        <div className="flex items-center gap-2 font-semibold text-content">
                                            {row.tenant.displayName}
                                            {row.tenant.isSandbox && (
                                                <Badge tone="dev">Sandbox</Badge>
                                            )}
                                        </div>
                                        <div className="mt-0.5 font-mono text-[11.5px] text-content-faint">
                                            {row.tenant.slug ?? row.tenant.tenantId}
                                        </div>
                                    </Td>
                                    <Td>
                                        <OutcomeBadge outcome={row.outcome} />
                                        {reason && (
                                            <div className="mt-1 max-w-[280px] text-[12px] text-content-muted">
                                                {reason}
                                            </div>
                                        )}
                                        <RowWarnings warnings={warnings} />
                                        <NotCopiedByDesign
                                            summary={notMigratedByDesignSummary(row)}
                                        />
                                    </Td>
                                    {columns.map((column) => (
                                        <Td
                                            key={column.key}
                                            className={
                                                column.align === 'right' ? 'text-right' : undefined
                                            }
                                        >
                                            {first ? rowRecordCells([column], first)[0]?.text : '—'}
                                        </Td>
                                    ))}
                                    {preview && (
                                        <Td className="text-[12px] text-content-muted">
                                            {(first && previewSummary(first.preview)) ?? '—'}
                                        </Td>
                                    )}
                                    {layout === 'migration' && (
                                        <>
                                            <Td>
                                                {runStatusText(row.run)}
                                                {runModuleTag(row.run, memberTitles) !== null && (
                                                    <Badge tone="neutral" className="ml-2">
                                                        {runModuleTag(row.run, memberTitles)}
                                                    </Badge>
                                                )}
                                            </Td>
                                            <Td className="text-[12px] text-content-muted">
                                                {runTotalsText(row.run) || '—'}
                                            </Td>
                                            <Td className="text-[12px]">
                                                {issuesText(issues) ?? '—'}
                                            </Td>
                                        </>
                                    )}
                                    {shape.expandable && (
                                        <Td>
                                            {row.recordsTotal > 0 ? (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        flip(id);
                                                    }}
                                                >
                                                    {isOpen ? 'Hide' : 'Show'}{' '}
                                                    {row.recordsTotal.toLocaleString('en-US')}
                                                </Button>
                                            ) : (
                                                '—'
                                            )}
                                        </Td>
                                    )}
                                </tr>
                                {shape.expandable && isOpen && (
                                    <tr>
                                        <td
                                            colSpan={span}
                                            className="border-b border-line-subtle bg-surface-sunken px-4 py-3"
                                        >
                                            <RecordList
                                                module={module}
                                                mode={mode}
                                                titles={memberTitles}
                                                row={row}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
            {hasMore && (
                <div className="flex justify-center px-4 py-3">
                    <Button variant="ghost" size="sm" onClick={onLoadMore} disabled={loadingMore}>
                        {loadingMore ? 'Loading…' : 'Load more clients'}
                    </Button>
                </div>
            )}
        </div>
    );
}

/**
 * The module's pre-run check findings, one per line. A blocking one is labelled
 * "Will block at launch" in red: the row still reads Migratable, but the launch
 * will refuse that client until the cause is fixed.
 */
function RowWarnings({ warnings }: { warnings: readonly RowWarning[] }) {
    if (warnings.length === 0) return null;
    return (
        <ul className="mt-1 flex max-w-[280px] flex-col gap-0.5 text-[12px]">
            {warnings.map((warning, index) => (
                <li
                    key={index}
                    className={warning.blocking ? 'text-danger-text' : 'text-warning-text'}
                >
                    <span className="font-semibold">{warningLabel(warning)}:</span>{' '}
                    <span className="font-mono text-[11.5px]">{warning.code}</span> —{' '}
                    {warning.message}
                </li>
            ))}
        </ul>
    );
}

/** The client's records not copied by design, with the count per reason code. */
function NotCopiedByDesign({ summary }: { summary: NotMigratedByDesignSummary | null }) {
    if (summary === null) return null;
    return (
        <div className="mt-1 max-w-[280px] text-[12px] text-content-muted">
            <div className="font-semibold">{summary.headline}</div>
            {summary.lines.length > 0 && (
                <ul className="mt-0.5 flex flex-col gap-0.5">
                    {summary.lines.map((line) => (
                        <li key={line.code}>{notMigratedByDesignLine(line)}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/** Every record the row carries — at most 100; `recordsTotal` says how many exist. */
function RecordList({
    module,
    mode,
    titles,
    row,
}: {
    module: BulkModule;
    mode: JobMode;
    titles: ReadonlyMap<string, string>;
    row: TenantRow;
}) {
    return (
        <div className="flex flex-col gap-2">
            {row.records.map((record) => (
                <RecordLine
                    key={`${record.moduleId ?? ''}:${record.sourceId}`}
                    module={module}
                    mode={mode}
                    titles={titles}
                    record={record}
                />
            ))}
            {row.recordsTotal > row.records.length && (
                <p className="text-[12px] text-content-muted">
                    Showing {row.records.length} of {row.recordsTotal.toLocaleString('en-US')}{' '}
                    records. Download the JSON for the runner&apos;s full report.
                </p>
            )}
        </div>
    );
}

function RecordLine({
    module,
    mode,
    titles,
    record,
}: {
    module: BulkModule;
    mode: JobMode;
    titles: ReadonlyMap<string, string>;
    record: RecordRow;
}) {
    const tag = recordModuleTag(record, titles);
    const preview = module.supportsPreview ? previewSummary(record.preview) : null;
    const planned = plannedWarningsShown(record, mode);
    return (
        <div className="text-[12.5px]">
            <span className="font-semibold text-content">{record.title}</span>{' '}
            <span className="font-mono text-[11px] text-content-faint">{record.sourceId}</span>
            {tag && (
                <Badge tone="neutral" className="ml-2">
                    {tag}
                </Badge>
            )}
            <span className="ml-2 text-content-muted">
                {rowRecordCells(module.columns, record)
                    // A column of another member reads blank: it is left out of the line.
                    .filter((cell) => cell.text !== '')
                    .map((cell) => `${cell.label}: ${cell.text}`)
                    .join(' · ')}
                {preview ? ` · ${preview}` : ''}
            </span>
            {record.notMigrated && (
                <span className="ml-2 text-warning-text">
                    Not migrated — {record.notMigrated.message} ({record.notMigrated.code})
                </span>
            )}
            {planned.map((warning, index) => (
                <div
                    key={`planned-${String(index)}`}
                    className={
                        warning.blocking ? 'mt-0.5 text-danger-text' : 'mt-0.5 text-warning-text'
                    }
                >
                    {plannedWarningLine(warning)}
                </div>
            ))}
            {recordIssueLines(record).map((line, index) => (
                <div key={index} className="mt-0.5 text-warning-text">
                    {line}
                </div>
            ))}
        </div>
    );
}
